/**
 * Where the conversation landed: the headline figures on the left, the final state
 * of every outcome capability on the right.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import type { SystemState } from "@unlikelyai-magic/utils";
import { OutcomeChip, outcomeChipState } from "../components";
import { type AuditTurn, checksRunOnTurn, draftFailed, draftsForTurn } from "./auditTurns";

const Figure = ({
  value,
  suffix,
  label,
  magenta = false,
}: {
  value: number;
  suffix?: string;
  label: string;
  magenta?: boolean;
}) => (
  <div className="flex flex-col gap-px">
    <span
      className={`text-xl font-semibold ${magenta ? "text-unlikely" : "text-brand-foreground"}`}
    >
      {value}
      {suffix && (
        <span className="text-xs font-normal text-brand-muted">{suffix}</span>
      )}
    </span>
    <span className="text-xs text-brand-muted">{label}</span>
  </div>
);

interface AuditLogSummaryProps {
  turns: AuditTurn[];
  systemState: SystemState;
}

export const AuditLogSummary = ({
  turns,
  systemState,
}: AuditLogSummaryProps) => {
  const outcomes = Array.from(systemState.suggestions.values());
  const decided = outcomes.filter(
    (outcome) =>
      outcome.value === Outcome.TRUE || outcome.value === Outcome.FALSE
  ).length;

  const stoppedReplies = turns.reduce(
    (total, turn) =>
      total + draftsForTurn(turn).filter(draftFailed).length,
    0
  );
  const checksRun = turns.reduce(
    (total, turn) => total + checksRunOnTurn(turn),
    0
  );

  return (
    <div className="flex flex-none gap-5 rounded-lg border border-brand-border bg-brand-background px-4 py-3.5">
      <div className="flex w-[300px] flex-none flex-col gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
          Where the conversation landed
        </h3>
        <div className="flex gap-5">
          <Figure
            value={decided}
            suffix={` / ${outcomes.length}`}
            label="outcomes decided"
          />
          <Figure
            value={stoppedReplies}
            label={stoppedReplies === 1 ? "reply stopped" : "replies stopped"}
            magenta={true}
          />
          <Figure value={checksRun} label="checks run" />
        </div>
      </div>

      <div className="w-px bg-brand-border" aria-hidden={true} />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand-muted">
          Final state of the outcome capabilities
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {outcomes.map((outcome) => (
            <OutcomeChip
              key={outcome.productTreeId}
              name={outcome.name}
              state={outcomeChipState(outcome.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
